const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const smoothstep = (value) => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};
const smootherstep = (value) => {
  const t = clamp(value);
  return t * t * t * (t * (t * 6 - 15) + 10);
};

class FyrasolScrollReveal extends HTMLElement {
  connectedCallback() {
    this.frames = [...this.querySelectorAll('.fyrasol-reveal__iris-frame')];
    this.sticky = this.querySelector('.fyrasol-reveal__sticky');
    this.ring = this.querySelector('.fyrasol-reveal__ring');
    this.logo = this.querySelector('.fyrasol-reveal__logo');
    this.angles = (this.dataset.ringAngles || '')
      .split(',')
      .map(Number)
      .filter(Number.isFinite);
    this.loaded = false;
    this.raf = 0;
    this.onScroll = this.onScroll.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);

    this.render(0);
    this.observe();
    reducedMotion.addEventListener?.('change', this.onMotionChange);

    window.__seekFyrasolReveal = (progress) => this.render(clamp(Number(progress) || 0));
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    reducedMotion.removeEventListener?.('change', this.onMotionChange);
    this.observer?.disconnect();
    cancelAnimationFrame(this.raf);
    if (window.__seekFyrasolReveal) delete window.__seekFyrasolReveal;
  }

  observe() {
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.loadFrames();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.observer.disconnect();
    }, { rootMargin: '70% 0px' });
    this.observer.observe(this);
  }

  async loadFrames() {
    if (this.loaded) return;
    this.loaded = true;

    const pending = this.frames.slice(1).map(async (frame) => {
      if (frame.dataset.src) frame.src = frame.dataset.src;
      try {
        await frame.decode();
        frame.dataset.loaded = 'true';
      } catch (_error) {
        frame.dataset.loaded = 'false';
      }
    });

    await Promise.allSettled(pending);
    this.dataset.framesReady = 'true';
    this.onScroll();
  }

  onMotionChange() {
    if (reducedMotion.matches) this.render(0);
    else this.onScroll();
  }

  onScroll() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const stickyHeight = this.sticky?.offsetHeight || window.innerHeight;
      const range = Math.max(1, this.offsetHeight - stickyHeight);
      const progress = reducedMotion.matches ? 0 : clamp(-rect.top / range);
      this.render(progress);
      this.raf = 0;
    });
  }

  angleAt(framePosition) {
    if (this.angles.length !== this.frames.length) return -75 + 75 * (framePosition / Math.max(1, this.frames.length - 1));
    const lower = Math.floor(framePosition);
    const upper = Math.min(this.angles.length - 1, lower + 1);
    const blend = smoothstep(framePosition - lower);
    return this.angles[lower] + (this.angles[upper] - this.angles[lower]) * blend;
  }

  render(progress) {
    if (!this.frames.length) return;

    // Leave a short mechanical hold at each end, then use a symmetric physical
    // curve so the scroll remains directly controllable in both directions.
    const activeProgress = clamp((progress - 0.06) / 0.86);
    const easedProgress = smootherstep(activeProgress);
    const framePosition = easedProgress * (this.frames.length - 1);
    const lower = Math.floor(framePosition);
    const upper = Math.min(this.frames.length - 1, lower + 1);
    const blend = smoothstep(framePosition - lower);

    this.frames.forEach((frame, index) => {
      const opacity = index === lower ? 1 - blend : index === upper ? blend : 0;
      frame.style.opacity = String(opacity);
    });

    if (this.ring) this.ring.style.transform = `rotate(${this.angleAt(framePosition)}deg)`;
    const logoProgress = smoothstep(clamp((easedProgress - 0.62) / 0.28));
    const veilProgress = smoothstep(clamp((easedProgress - 0.7) / 0.3));
    this.style.setProperty('--fyrasol-reveal-progress', String(easedProgress));
    this.style.setProperty('--fyrasol-logo-progress', String(logoProgress));
    this.style.setProperty('--fyrasol-veil-progress', String(veilProgress));
    this.style.setProperty('--fyrasol-product-scale', String(0.96 + easedProgress * 0.04));
    this.style.setProperty('--fyrasol-ambient-opacity', String(0.66 + easedProgress * 0.34));
    this.style.setProperty('--fyrasol-logo-scale', String(0.82 + logoProgress * 0.18));
    this.style.setProperty('--fyrasol-logo-blur', `${(1 - logoProgress) * 7}px`);
    this.style.setProperty('--fyrasol-veil-opacity', String(0.42 + veilProgress * 0.18));
    this.style.setProperty('--fyrasol-veil-blur', `${veilProgress * 2}px`);
    this.dataset.state = easedProgress <= 0 ? 'closed' : easedProgress >= 1 ? 'open' : 'opening';
  }
}

if (!customElements.get('fyrasol-scroll-reveal')) {
  customElements.define('fyrasol-scroll-reveal', FyrasolScrollReveal);
}
